import { Button, Empty, Skeleton, Table } from "antd";
import type { TableProps } from "antd";
import { useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { publicApi } from "../services/api";
import { QueryError, SectionHeading, WorkCard } from "./Editorial";
import { MONTHS, formatMinutes } from "../utils/period";

export function StatsSummary({
  year,
  month,
}: {
  year?: number;
  month?: number;
}) {
  const query = useQuery({
    queryKey: ["public-library-stats", year, month],
    queryFn: () => publicApi.getStats(year, month),
  });
  if (query.isError) return <QueryError retry={() => void query.refetch()} />;
  if (!query.data) return <Skeleton active paragraph={{ rows: 2 }} />;
  const data = query.data;
  const metrics = [
    ["Książki", data.books_completed.toLocaleString("pl-PL"), "book"],
    ["Filmy", data.movies_completed.toLocaleString("pl-PL"), "movie"],
    [
      "Ukończone sezony",
      data.series_completed.toLocaleString("pl-PL"),
      "series",
    ],
    ["Przeczytane strony", data.pages_read.toLocaleString("pl-PL"), "book"],
    [
      "Czas oglądania",
      data.minutes_watched ? formatMinutes(data.minutes_watched) : "0 min",
      "movie",
    ],
  ];
  return (
    <div className="stats-summary">
      {metrics.map(([label, value, type]) => (
        <div key={label} className={`summary-item summary-${type}`}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

export function YearChart({
  year,
  onMonth,
}: {
  year: number;
  onMonth: (month: number) => void;
}) {
  const query = useQuery({
    queryKey: ["public-library-monthly", year],
    queryFn: () => publicApi.getMonthly(year),
  });
  return (
    <section className="chart-panel">
      <SectionHeading
        eyebrow="Rytm roku"
        title={`Dwanaście miesięcy, wiele historii · ${year}`}
        description="Wybierz miesiąc na wykresie lub w przyciskach poniżej."
      />
      {query.isError ? (
        <QueryError retry={() => void query.refetch()} />
      ) : !query.data ? (
        <Skeleton active />
      ) : (
        <>
          <div className="year-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={query.data.items}
                margin={{ left: -20, right: 8, top: 12 }}
              >
                <CartesianGrid vertical={false} stroke="#E2E3D9" />
                <XAxis
                  dataKey="month"
                  tickFormatter={(value) =>
                    MONTHS[value - 1]?.slice(0, 3) ?? ""
                  }
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  labelFormatter={(value) => MONTHS[Number(value) - 1] ?? ""}
                />
                <Legend />
                <Bar
                  isAnimationActive={false}
                  dataKey="books_completed"
                  name="Książki"
                  stackId="works"
                  fill="#245747"
                  onClick={(data) => onMonth(Number(data.payload.month))}
                  cursor="pointer"
                />
                <Bar
                  isAnimationActive={false}
                  dataKey="movies_completed"
                  name="Filmy"
                  stackId="works"
                  fill="#4B6B88"
                  onClick={(data) => onMonth(Number(data.payload.month))}
                  cursor="pointer"
                />
                <Bar
                  isAnimationActive={false}
                  dataKey="series_completed"
                  name="Sezony seriali"
                  stackId="works"
                  fill="#A6543C"
                  onClick={(data) => onMonth(Number(data.payload.month))}
                  cursor="pointer"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="month-links" aria-label="Przejdź do miesiąca">
            {MONTHS.map((name, i) => (
              <button
                key={name}
                onClick={() => onMonth(i + 1)}
                aria-label={`${name} ${year}`}
              >
                {name.slice(0, 3)}
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

const SECTION_TITLES = {
  movie: "Obejrzane filmy",
  book: "Przeczytane książki",
  series: "Ukończone sezony",
};

export function MonthlyWorks({
  year,
  month,
  type,
}: {
  year: number;
  month: number;
  type: keyof typeof SECTION_TITLES;
}) {
  const query = useInfiniteQuery({
    queryKey: ["monthly-works", year, month, type],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      publicApi.listLibrary({
        year,
        month,
        type,
        page: pageParam,
        page_size: 12,
      }),
    getNextPageParam: (last) =>
      last.page < last.total_pages ? last.page + 1 : undefined,
  });
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const total = query.data?.pages[0]?.total;
  return (
    <section className="monthly-works" aria-label={SECTION_TITLES[type]}>
      <SectionHeading
        eyebrow={MONTHS[month - 1]}
        title={SECTION_TITLES[type]}
        action={
          total !== undefined && <span className="section-count">{total}</span>
        }
      />
      {query.isPending && <Skeleton active />}
      {query.isError && (
        <QueryError
          retry={() => {
            if (query.isFetchNextPageError) void query.fetchNextPage();
            else void query.refetch();
          }}
        />
      )}
      {query.data &&
        (total === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="W tym miesiącu nie ma ukończonych pozycji tego typu."
          />
        ) : (
          <>
            <div className="work-grid monthly-grid">
              {items.map((entry) => (
                <WorkCard key={entry.id} entry={entry} compact />
              ))}
            </div>
            <div className="load-more">
              <span aria-live="polite">
                Pokazano {items.length} z {total}
              </span>
              {query.hasNextPage && (
                <Button
                  loading={query.isFetchingNextPage}
                  onClick={() => void query.fetchNextPage()}
                >
                  Pokaż kolejne
                </Button>
              )}
            </div>
          </>
        ))}
    </section>
  );
}

export function RankingTable<T extends object>(props: TableProps<T>) {
  const [expanded, setExpanded] = useState(false);
  const all = props.dataSource ?? [];
  return (
    <>
      <Table<T>
        {...props}
        dataSource={expanded ? all : all.slice(0, 10)}
        summary={props.summary ? () => props.summary!(all) : undefined}
        pagination={false}
        scroll={{ x: "max-content", ...props.scroll }}
      />
      {all.length > 10 && (
        <Button
          className="ranking-expand"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Zwiń ranking" : `Pokaż wszystkie (${all.length})`}
        </Button>
      )}
    </>
  );
}
